import { BaseModal } from '@/components/shared';

interface ConfirmarEquipamentoModalProps {
  isOpen: boolean;
  titulo: string;
  pergunta: string;
  onResponder: (sim: boolean) => void;
  onClose: () => void;
}

/**
 * Modal Sim/Não genérico, reaproveitado nos 3 pontos do fluxo pós-alocação que só
 * precisam de uma confirmação (registrar alocação nova, saída de projeto, troca de
 * projeto) — só muda título/pergunta.
 */
export function ConfirmarEquipamentoModal({
  isOpen,
  titulo,
  pergunta,
  onResponder,
  onClose,
}: ConfirmarEquipamentoModalProps) {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={titulo}
      maxWidth="max-w-md"
      fullHeight={false}
      dismissible={false}
      actions={
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={() => onResponder(false)}>
            Não
          </button>
          <button type="button" className="btn-primary" onClick={() => onResponder(true)}>
            Sim
          </button>
        </div>
      }
    >
      <p className="py-2 text-sm text-gray-700">{pergunta}</p>
    </BaseModal>
  );
}

export type MotivoEncerramentoAlocacao = 'desligamento' | 'saida_projeto' | 'mudanca_projeto';

interface MotivoEncerramentoModalProps {
  isOpen: boolean;
  onEscolher: (motivo: MotivoEncerramentoAlocacao) => void;
  onClose: () => void;
  /** Se o colaborador tem outra(s) alocação(ões) ativa(s) além desta que está sendo
   * encerrada — muda a descrição de "Saída do Projeto", que senão soa como se ele ficasse
   * sem nenhum projeto (pode não ser o caso). */
  temOutrasAlocacoesAtivas?: boolean;
}

function montarOpcoesMotivo(
  temOutrasAlocacoesAtivas: boolean
): Array<{ valor: MotivoEncerramentoAlocacao; label: string; descricao: string }> {
  return [
    {
      valor: 'desligamento',
      label: 'Desligamento',
      descricao: 'O colaborador está saindo da empresa — todos os equipamentos serão devolvidos.',
    },
    {
      valor: 'saida_projeto',
      label: 'Saída do Projeto',
      descricao: temOutrasAlocacoesAtivas
        ? 'Sai deste projeto — o colaborador continua alocado em outro(s) projeto(s).'
        : 'Fica sem projeto definido por enquanto.',
    },
    {
      valor: 'mudanca_projeto',
      label: 'Mudança de Projeto',
      descricao: 'Já vai para outro projeto agora.',
    },
  ];
}

/** Aberto quando a data de término de uma alocação passa de vazia pra preenchida — ver
 * CollaboratorAllocationFormPage. */
export function MotivoEncerramentoModal({
  isOpen,
  onEscolher,
  onClose,
  temOutrasAlocacoesAtivas = false,
}: MotivoEncerramentoModalProps) {
  const opcoes = montarOpcoesMotivo(temOutrasAlocacoesAtivas);
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Qual o motivo da alteração?"
      maxWidth="max-w-md"
      fullHeight={false}
      dismissible={false}
    >
      <div className="space-y-2 py-2">
        {opcoes.map((opcao) => (
          <button
            key={opcao.valor}
            type="button"
            className="w-full rounded-md border border-gray-200 px-4 py-3 text-left hover:bg-gray-50"
            onClick={() => onEscolher(opcao.valor)}
          >
            <p className="text-sm font-medium text-gray-900">{opcao.label}</p>
            <p className="mt-0.5 text-xs text-gray-500">{opcao.descricao}</p>
          </button>
        ))}
      </div>
    </BaseModal>
  );
}
